import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Coins, Trophy, Sparkles, ShieldCheck, Star, Gem, Share2, Copy, Users, Loader2, ArrowUpRight, MessageSquareWarning, Zap } from "lucide-react";

const TIER_ICONS = {
  5: { Icon: Star,      chip: "from-zinc-300 to-zinc-500",   fg: "text-zinc-900", ring: "ring-zinc-300/40" },
  10: { Icon: ShieldCheck, chip: "from-[#F5C462] to-[#B8860B]", fg: "text-black",     ring: "ring-[#F5C462]/40" },
  20: { Icon: Gem,      chip: "from-[#7dd3fc] to-[#8b5cf6]",  fg: "text-black",     ring: "ring-[#8b5cf6]/40" },
};

export default function Rewards() {
  const { user, refresh } = useAuth();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [refs, setRefs] = useState([]);
  const [board, setBoard] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, h, r, b] = await Promise.all([
        api.get("/rewards/summary"),
        api.get("/rewards/history"),
        api.get("/rewards/referrals"),
        api.get("/rewards/leaderboard"),
      ]);
      setSummary(s.data);
      setHistory(h.data.history || []);
      setRefs(r.data.referrals || []);
      setBoard(b.data.leaderboard || []);
    } catch (err) {
      toast.error(errMsg(err, "Could not load rewards"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const refCode = summary?.referral_code || user?.referral_code;
  const link = refCode ? `${window.location.origin}/signup?ref=${refCode}` : "";

  const copyLink = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    toast.success("Referral link copied");
  };
  const share = async () => {
    if (!link) return;
    const text = `Join me on NextScene — get discovered as a Bharat talent. Use my code ${refCode}: ${link}`;
    if (navigator.share) { try { await navigator.share({ title: "NextScene", text, url: link }); return; } catch {} }
    await navigator.clipboard.writeText(text);
    toast.success("Share text copied");
  };
  const spend = async (feature) => {
    setBusy(feature);
    try {
      const { data } = await api.post("/rewards/spend", { feature });
      toast.success(`Unlocked — ${data.coins} coins left`);
      await load();
      await refresh?.();
    } catch (err) {
      toast.error(errMsg(err, "Could not spend coins"));
    } finally { setBusy(null); }
  };

  if (loading) {
    return <div className="min-h-screen"><TopBar subtitle="Rewards"/><div className="p-6 text-zinc-400 text-sm">Loading rewards…</div></div>;
  }

  const qualified = summary?.referrals_qualified || 0;
  const pending = summary?.referrals_pending || 0;
  const target = summary?.next_target;
  const toNext = summary?.to_next || 0;
  const progressPct = target ? Math.min(100, (qualified / target) * 100) : 100;

  return (
    <div data-testid="rewards-page" className="min-h-screen pb-24">
      <TopBar subtitle="Rewards"/>
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        <BackButton fallback="/app" label="Back"/>

        {/* Coin balance card */}
        <div className="rounded-3xl border border-[#F5C462]/30 bg-gradient-to-br from-[#2a1f0e] via-[#180D0D] to-[#0A0605] p-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#F5C462]/15 blur-3xl"/>
          <p className="text-xs uppercase tracking-[0.2em] text-[#F5C462] flex items-center gap-1"><Coins size={12}/> NextScene Coins</p>
          <div className="mt-2 flex items-end gap-2">
            <span data-testid="coin-balance" className="font-display text-5xl font-bold text-white">{summary?.coins ?? 0}</span>
            <span className="pb-2 text-sm text-zinc-400">coins in wallet</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-zinc-900/70 border border-zinc-800 px-3 py-1 text-zinc-300 inline-flex items-center gap-1"><Users size={11}/> {qualified} qualified</span>
            <span className="rounded-full bg-zinc-900/70 border border-zinc-800 px-3 py-1 text-zinc-400 inline-flex items-center gap-1">{pending} pending</span>
            {summary?.silver_badge && <span className="rounded-full bg-gradient-to-r from-zinc-200 to-zinc-400 text-black font-semibold px-3 py-1 inline-flex items-center gap-1"><Star size={11}/> Silver</span>}
          </div>
        </div>

        {/* Progress to next milestone */}
        {target ? (
          <div data-testid="progress-card" className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
            <p className="text-xs uppercase tracking-wider text-zinc-400">Next milestone</p>
            <p className="mt-1 text-lg font-semibold text-white">Only <span className="text-[#DC2626]">{toNext}</span> more {toNext === 1 ? "invite" : "invites"} to unlock <span className="text-[#F5C462]">{summary?.tiers?.find(t => t.count === target)?.label}</span>!</p>
            <div className="mt-3 h-3 rounded-full bg-zinc-800 overflow-hidden">
              <div data-testid="progress-bar" style={{ width: `${progressPct}%` }} className="h-full bg-gradient-to-r from-[#DC2626] via-[#F5C462] to-[#EF4444] transition-all"/>
            </div>
            <p className="mt-2 text-xs text-zinc-400">{qualified} of {target} qualified referrals</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-5 text-emerald-300 text-sm">
            🎉 You've unlocked every tier. Keep referring to top the leaderboard.
          </div>
        )}

        {/* Invite / Share card */}
        <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
          <p className="text-xs uppercase tracking-wider text-[#DC2626] flex items-center gap-1"><Share2 size={12}/> Invite friends</p>
          <p className="mt-1 text-sm text-zinc-300">Share your link. When your friend verifies email and uploads a 15s+ performance, you both win.</p>
          <div className="mt-3 flex items-center gap-2">
            <Input data-testid="ref-link" readOnly value={link} className="bg-zinc-900 border-zinc-800 text-xs"/>
            <Button data-testid="ref-copy" onClick={copyLink} className="h-10 rounded-full bg-zinc-900 border border-zinc-700 hover:border-[#DC2626]/50"><Copy size={14}/></Button>
            <Button data-testid="ref-share" onClick={share} className="h-10 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">Share</Button>
          </div>
          <p className="mt-3 text-[11px] text-zinc-500 leading-relaxed border-l-2 border-[#DC2626]/40 pl-2">
            <MessageSquareWarning size={11} className="inline mr-1 text-[#DC2626]"/> Referral is only valid after the invited friend <span className="text-white">verifies their email</span> AND <span className="text-white">uploads their first performance video (15s+)</span>.
          </p>
          <Link to="/app/referral-card" data-testid="ref-storycard-link" className="mt-3 inline-flex items-center gap-1 text-xs text-[#DC2626] hover:underline font-semibold">
            Generate share-ready story card <ArrowUpRight size={12}/>
          </Link>
        </div>

        {/* Milestone tiers */}
        <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
          <p className="text-xs uppercase tracking-wider text-[#DC2626] flex items-center gap-1"><Trophy size={12}/> Milestone rewards</p>
          <div className="mt-4 grid gap-3">
            {summary?.tiers?.map((t) => {
              const { Icon, chip, fg, ring } = TIER_ICONS[t.count] || TIER_ICONS[5];
              return (
                <div key={t.count} data-testid={`tier-${t.count}`} className={`relative rounded-2xl border p-4 flex items-center gap-4 ${t.unlocked ? "border-emerald-500/40 bg-emerald-500/5" : "border-zinc-800 bg-black/40"}`}>
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${chip} flex items-center justify-center ring-4 ${ring} shrink-0`}>
                    <Icon size={22} className={fg}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-400">Refer {t.count} friends</p>
                    <p className="font-display text-lg font-semibold text-white leading-tight">{t.label}</p>
                    <p className="text-xs text-zinc-300 mt-0.5">{t.perk}</p>
                  </div>
                  {t.unlocked ? (
                    <span className="text-xs rounded-full bg-emerald-500/20 text-emerald-400 px-2 py-1 font-semibold">✓ Unlocked</span>
                  ) : (
                    <span className="text-xs rounded-full bg-zinc-800 text-zinc-400 px-2 py-1">{Math.max(0, t.count - t.current)} to go</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Coin spend */}
        <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
          <p className="text-xs uppercase tracking-wider text-[#DC2626] flex items-center gap-1"><Sparkles size={12}/> Spend your coins</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <PerkCard
              testid="spend-extra-upload"
              title="Extra upload slot"
              desc="Bypass the monthly cap for one more performance."
              price={summary?.coin_prices?.extra_upload?.cost || 100}
              have={summary?.coins || 0}
              busy={busy === "extra_upload"}
              onBuy={() => spend("extra_upload")}
              badge={summary?.extra_upload_credits ? `${summary.extra_upload_credits} banked` : null}
              Icon={Zap}
            />
            <PerkCard
              testid="spend-priority-alerts"
              title="Priority casting alerts"
              desc="Push notifications for 24 hours — even Free users."
              price={summary?.coin_prices?.priority_alerts?.cost || 200}
              have={summary?.coins || 0}
              busy={busy === "priority_alerts"}
              onBuy={() => spend("priority_alerts")}
              badge={summary?.priority_alerts_until ? "ON" : null}
              Icon={MessageSquareWarning}
            />
            <PerkCard
              testid="spend-story-card"
              title="Premium Story Card"
              desc="Gold-accented shareable card for 7 days."
              price={summary?.coin_prices?.story_card_boost?.cost || 150}
              have={summary?.coins || 0}
              busy={busy === "story_card_boost"}
              onBuy={() => spend("story_card_boost")}
              badge={summary?.story_card_boost_until ? "PREMIUM" : null}
              Icon={Sparkles}
            />
            <PerkCard
              testid="spend-spotlight"
              title="Featured spotlight (24h)"
              desc="Get placed in the Home Featured strip for a full day."
              price={summary?.coin_prices?.profile_spotlight?.cost || 300}
              have={summary?.coins || 0}
              busy={busy === "profile_spotlight"}
              onBuy={() => spend("profile_spotlight")}
              badge={summary?.spotlight_until ? "LIVE" : null}
              Icon={Trophy}
            />
            <PerkCard
              testid="spend-headliner"
              title="Discover headliner (48h)"
              desc="Pin your profile atop Discover for two days."
              price={summary?.coin_prices?.cast_headliner?.cost || 400}
              have={summary?.coins || 0}
              busy={busy === "cast_headliner"}
              onBuy={() => spend("cast_headliner")}
              badge={summary?.headliner_until ? "PINNED" : null}
              Icon={Star}
            />
          </div>
        </div>

        {/* Weekly leaderboard */}
        <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
          <p className="text-xs uppercase tracking-wider text-[#DC2626] flex items-center gap-1"><Trophy size={12}/> Weekly leaderboard</p>
          {board.length === 0 ? (
            <p className="mt-3 text-sm text-zinc-400">No qualified referrals this week yet. Be the first.</p>
          ) : (
            <ol className="mt-3 space-y-2" data-testid="leaderboard">
              {board.map((p, idx) => (
                <li key={p.user_id} className="flex items-center gap-3 py-2 border-b border-zinc-900 last:border-0">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${idx === 0 ? "bg-[#F5C462] text-black" : idx === 1 ? "bg-zinc-300 text-black" : idx === 2 ? "bg-amber-700/70 text-white" : "bg-zinc-800 text-zinc-300"}`}>{idx + 1}</span>
                  <Link to={`/${p.username}`} className="flex-1 min-w-0 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#DC2626]/20 border border-[#DC2626]/40 flex items-center justify-center text-[#DC2626] text-xs font-semibold shrink-0">{(p.name || p.username || "?").slice(0,1).toUpperCase()}</div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white truncate flex items-center gap-1">{p.name}{p.silver_badge && <Star size={10} className="text-zinc-300 fill-zinc-300"/>}</p>
                      <p className="text-[11px] text-zinc-500">@{p.username}</p>
                    </div>
                  </Link>
                  <span className="text-sm font-bold text-[#F5C462] shrink-0">{p.qualified_this_week}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Referral list */}
        <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
          <p className="text-xs uppercase tracking-wider text-[#DC2626] flex items-center gap-1"><Users size={12}/> Referral history</p>
          {refs.length === 0 ? (
            <p className="mt-3 text-sm text-zinc-400">No one has joined via your link yet.</p>
          ) : (
            <ul data-testid="referral-list" className="mt-3 space-y-2">
              {refs.map((r) => (
                <li key={r.id} className="flex items-center gap-3 py-2 border-b border-zinc-900 last:border-0">
                  <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-zinc-300 shrink-0">{(r.referee?.name || r.referee?.username || "?").slice(0,1).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">@{r.referee?.username || r.referee_username}</p>
                    <p className="text-[11px] text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  {r.status === "qualified" ? (
                    <span className="text-[10px] rounded-full bg-emerald-500/20 text-emerald-400 px-2 py-0.5 font-semibold">✓ +{10} coins</span>
                  ) : (
                    <span className="text-[10px] rounded-full bg-zinc-800 text-zinc-400 px-2 py-0.5">pending</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Coin history */}
        {history.length > 0 && (
          <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
            <p className="text-xs uppercase tracking-wider text-[#DC2626] flex items-center gap-1"><Coins size={12}/> Coin history</p>
            <ul data-testid="coin-history" className="mt-3 space-y-1.5">
              {history.map((h) => (
                <li key={h.id} className="flex items-center justify-between text-xs">
                  <div className="min-w-0">
                    <p className="text-zinc-200 truncate">{h.reason}</p>
                    <p className="text-[10px] text-zinc-500">{new Date(h.created_at).toLocaleString()}</p>
                  </div>
                  <span className={`font-semibold shrink-0 ml-3 ${h.delta > 0 ? "text-emerald-400" : "text-red-400"}`}>{h.delta > 0 ? "+" : ""}{h.delta}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function PerkCard({ testid, title, desc, price, have, busy, onBuy, badge, Icon }) {
  const enough = have >= price;
  return (
    <div className={`rounded-2xl border p-4 ${enough ? "border-[#DC2626]/40 bg-[#DC2626]/5" : "border-zinc-800 bg-black/40"} flex flex-col`}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-xl bg-[#DC2626]/15 border border-[#DC2626]/40 flex items-center justify-center text-[#DC2626]"><Icon size={16}/></div>
        {badge && <span className="text-[10px] rounded-full bg-emerald-500/20 text-emerald-400 px-2 py-0.5 font-semibold">{badge}</span>}
      </div>
      <p className="mt-2 font-display font-semibold text-white">{title}</p>
      <p className="mt-0.5 text-xs text-zinc-400 flex-1">{desc}</p>
      <Button data-testid={testid} onClick={onBuy} disabled={!enough || busy} className={`mt-3 h-10 rounded-full font-semibold ${enough ? "bg-[#DC2626] hover:bg-[#EF4444] text-white" : "bg-zinc-800 text-zinc-500"}`}>
        {busy ? <Loader2 className="animate-spin" size={14}/> : <>Unlock · <span className="ml-1 text-[#F5C462]">{price}</span> <Coins size={12} className="ml-1"/></>}
      </Button>
    </div>
  );
}
